import AddIcon from '@mui/icons-material/Add';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { ButtonGroup } from '@mui/material';
import { DataGridPremiumProps, GridColDef } from '@mui/x-data-grid-premium';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import type { ToolbarAction, ToolbarFilter } from 'components/enhanced/types';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import { useIsSupplier } from 'shared/permissions/useIsSupplier';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import TenantPage from 'layouts/TenantPage';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { contractEquipmentService, maintenanceService } from 'shared/services';
import { useAppSelector } from 'store/store';
import toast from 'shared/utils/toast';
import { IContractEquipment } from '../../contracts/types';
import { IEquipment } from '../../equipments/types';
import MaintenanceDetailPanel from '../components/MaintenanceDetailPanel';
import CloseMaintenanceDialog from '../components/dialog/CloseMaintenanceDialog';
import { AdvancedMaintenancePlanner } from '../components/dialog/MaintenanceCreateDialog';
import { useAggregatedColumns } from '../hooks/useaggregatedColumns';
import { LinkButton } from 'components/ui';
import { MaintenanceColumns } from '../maintenances';

const TRANSLATION_BASE_PREFIX = 'pages.maintenance';

/**
 * Props for the Maintenance component.
 * @property isTabView - Whether the component renders as a tab view (detail) or a full page (overview).
 * @property contextEquipment - Optional equipment context passed from the parent, used for outer filtering.
 */
interface MaintenanceProp {
  isTabView?: boolean;
  equipment?: { id: number | string; code?: string } | IEquipment | null;
  activeContrantEquipment?: IContractEquipment;
}

/**
 * Displays maintenance records either as a tab detail or as a full tenant page.
 * Handles dialogs for scheduling multiple dates, programming monthly maintenance,
 * and closing maintenance records.
 */
export const Maintenance = ({ isTabView, equipment, activeContrantEquipment }: MaintenanceProp) => {
  const { t } = useTranslation();
  const datagridRefresh = useRef(null);
  const [searchParams] = useSearchParams();

  // Evaluate role selectors unconditionally to keep React hook order stable.
  const technician = useIsTechnician();

  const userWarehouse = useAppSelector((state) => state.auth.warehouse);
  const lockWarehouseFilter = Boolean(userWarehouse) && technician;

  // -- Filters
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);

  // -- Permissions
  const canViewArchive = useIsTenantAdministrator() || useIsDirector();
  const isDisabledEdit = useIsTechnician() || useIsDirector() || useIsSupplier();

  // ---------------------------------------------------- STATI PER PLANNER AVANZATO

  const [openPlanner, setOpenPlanner] = useState(false);
  const [selectedContractEquipmentId, setSelectedContractEquipmentId] = useState<number | null>(
    null
  );

  /** Apre il dialog di pianificazione salvando l'ID della macchina/contratto */
  const onOpenPlannerDialog = (contractEquipmentId?: number) => {
    setSelectedContractEquipmentId(contractEquipmentId);
    setOpenPlanner(true);
  };

  /** Chiude il dialog di pianificazione e resetta l'ID selezionato */
  const onCancelPlannerDialog = () => {
    setOpenPlanner(false);
    setSelectedContractEquipmentId(null);
  };

  /** Gestisce il payload finale generato dalle 3 modalità e chiama il backend */
  const handleSavePlanner = (payload: { mode: string; dates: string[]; meta?: any }) => {
    if (!selectedContractEquipmentId) return;

    // Chiamata al servizio passando l'ID dell'equipaggiamento e l'array flat di date 'YYYY-MM-DD'
    contractEquipmentService
      .addMaintenanceDates(selectedContractEquipmentId, payload.dates)
      .then(() => {
        toast.success(t(`actions.successfully.created`));
        datagridRefresh.current?.(); // Ricarica la tabella
        onCancelPlannerDialog(); // Chiude il popup
      })
      .catch((error) => {
        console.error('Errore durante il salvataggio della pianificazione:', error);
      });
  };

  // ---------------------------------------------------- Close Maintenance Dialog

  const [openCloseMaintenance, setOpenCloseMaintenance] = useState(false);
  const [maintenanceCloseMaintenanceId, setMaintenanceCloseMaintenanceId] = useState<number | null>(
    null
  );

  const setOpenCloseMaintenanceDialog = (id: number) => {
    setOpenCloseMaintenance(true);
    setMaintenanceCloseMaintenanceId(id);
  }; /** Opens the dialog to close a maintenance record. */

  /** Closes the maintenance closing dialog and resets the related ID. */
  const setCloseMaintenanceDialog = () => {
    setOpenCloseMaintenance(false);
    setMaintenanceCloseMaintenanceId(null);
  };

  // ---------------------------------------------------- Maintenance Detail and Columns

  const columns: GridColDef[] = MaintenanceColumns(setOpenCloseMaintenanceDialog);
  const aggregatedColumns: GridColDef[] = useAggregatedColumns(isTabView, lockWarehouseFilter);

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps['getDetailPanelContent']>
  >(({ row }) => <MaintenanceDetailPanel row={{ ...row }} columns={columns} />, [columns]);

  const toolbarFilters: ToolbarFilter[] = React.useMemo(
    () => [
      {
        id: 'expected_month',
        type: 'dateRange',
        value: dateRange,
        onChange: (val) => setDateRange(val),
        gridField: 'expected_month',
        gridOperators: ['onOrAfter', 'onOrBefore']
      }
    ],
    [dateRange]
  );

  const customActions = [
    {
      id: 'add',
      label: 'Aggiungi Manutenzione',
      icon: <EventAvailableIcon fontSize="small" sx={{ color: '#334155' }} />,
      onClick: (row) => onOpenPlannerDialog(row.contract_equipment_id),
      visible: !isDisabledEdit
    }
  ];

  const toolbarActions: ToolbarAction[] = [
    {
      id: 'add',
      label: 'Aggiungi Manutenzione',
      variant: 'contained',
      startIcon: <AddIcon />,
      onClick: () => onOpenPlannerDialog(Number(activeContrantEquipment?.id)),
      visible: !isDisabledEdit && Boolean(activeContrantEquipment)
    }
  ];
  return (
    <>
      {isTabView ? (
        <EnhancedDataGrid
          columns={columns}
          service={maintenanceService}
          warehouseFilterParam="warehouse_code"
          initialParams={{ equipment_id: searchParams.get('equipment_id') }}
          refresh={datagridRefresh}
          disableDelete
          outerParams={{
            equipment: equipment?.code
          }}
          useGenericToolbar={true}
          genericToolbarConfig={{
            filters: toolbarFilters,
            actions: toolbarActions
          }}
          showGridControls={!isTabView}
        />
      ) : (
        <TenantPage
          title={t(`${TRANSLATION_BASE_PREFIX}.title`)}
          subtitle={t(`${TRANSLATION_BASE_PREFIX}.subtitle`)}
          menuRight={
            <ButtonGroup>
              <LinkButton to="expiring" prefix={TRANSLATION_BASE_PREFIX} label={'expiring'} />{' '}
              {canViewArchive && <LinkButton to="archive" prefix={'global'} label={'archive'} />}
            </ButtonGroup>
          }>
          <EnhancedDataGrid
            columns={aggregatedColumns}
            service={contractEquipmentService}
            rowActions={customActions}
            getMethod={contractEquipmentService.aggregatedContract}
            warehouseFilterParam="warehouse_code"
            initialParams={{ equipment_id: searchParams.get('equipment_id') }}
            refresh={datagridRefresh}
            getDetailPanelContent={getDetailPanelContent}
            disableDelete
          />
        </TenantPage>
      )}

      <CloseMaintenanceDialog
        onClose={setCloseMaintenanceDialog}
        datagridRefresh={datagridRefresh}
        maintenanceId={maintenanceCloseMaintenanceId}
        isOpen={openCloseMaintenance}
      />

      <AdvancedMaintenancePlanner
        onSave={handleSavePlanner}
        open={openPlanner}
        onClose={onCancelPlannerDialog}
      />
    </>
  );
};

export default Maintenance;
